import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { Fare } from '@/src/types/itinerary'
import { createJourneyTabs } from '@/src/utils/itinerary/fareFamily'
import { useLocale } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import useClevertapForItinerary from '../../analytics/clevertap/itinerary'
import useFormatCurrencyForItinerary from '../../currency/useFormatCurrencyForItinerary'
import useFareFamilyInfoApiCall from '../useFareFamilyInfoApiCall'
import useGetItineraryId from '../useGetItineraryId'
import useBenefitProcessor from './useBenefitProcessor'
import useFareRuleProcessor from './useFareRuleProcessor'

const metaPartners = ['wego', 'wegoeg']

const useTransparencyWidget = ({ initialTabValue = 0 }: { initialTabValue: 0 | 1 }) => {
    // State management
    const [tabValue, setTabValue] = useState<0 | 1>(initialTabValue)
    const [fareRuleExpanded, setFareRuleExpanded] = useState<number[]>([])

    // Hooks and utilities
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const itineraryId = useGetItineraryId()
    const { formatDate } = useFormatDate()
    const locale = useLocale()

    // API calls
    const {
        meta: { fareFamilyEnabledOnReview, utmSource },
        fares,
        isRoundTrip
    } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data }) => ({
                meta: data?.meta || {},
                fares: data?.fareFamilyFares || [],
                isRoundTrip: !!data?.searchCriteria?.arrivalDate
            })
        }
    )
    const openFareFamilyByDefault = fareFamilyEnabledOnReview && metaPartners.includes(utmSource)
    const [showFareFamilyCards, setShowFareFamilyCards] = useState(openFareFamilyByDefault)

    const {
        data: fareFamilyInfoData,
        isError,
        isLoading: isFareFamilyInfoLoading,
        isFetching: isFareFamilyInfoFetching
    } = useFareFamilyInfoApiCall({
        skip: !fareFamilyEnabledOnReview,
        selectFromResult: ({
            data,
            isLoading,
            isFetching,
            isError
        }: {
            data: any
            isLoading: boolean
            isFetching: boolean
            isError: boolean
        }) => ({
            data,
            isLoading,
            isFetching,
            isError
        })
    })

    // Derived state
    const selectedFare = useMemo(() => fares.find(fare => fare.selected) as Fare, [fares])
    const isLoading = isFareFamilyInfoLoading || isFareFamilyInfoFetching
    const showEditFares = fares?.length > 1

    // Helper hooks
    const { processBenefits } = useBenefitProcessor({ locale })
    const { processedFareRules } = useFareRuleProcessor({
        fareFamilyInfoData,
        tabValue,
        selectedFare,
        formatDate,
        formatCurrency
    })

    // Event handlers
    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: 0 | 1) => {
        setTabValue(newValue)
    }, [])

    const handleFareRuleExpanded = (index: number, type: string) => {
        setFareRuleExpanded(prev => {
            if (prev.includes(index)) {
                triggerItineraryEvent('Air_UI_Action', {
                    action_name: 'fare_family_rule_collapsed',
                    action_type: type
                })
                return prev.filter(i => i !== index)
            }
            triggerItineraryEvent('Air_UI_Action', {
                action_name: 'fare_family_rule_expanded',
                action_type: type
            })
            return [...prev, index]
        })
    }

    const closeFareFamilyModal = () => {
        setShowFareFamilyCards(false)
    }

    const openFareFamilyModal = () => {
        triggerItineraryEvent('Air_UI_Action', {
            action_name: 'edit_fare_clicked',
            action_type: 'BUTTON'
        })
        setShowFareFamilyCards(true)
    }

    // Computed values
    const journeyTabs = useMemo(() => {
        const solutions = fareFamilyInfoData?.data?.solutions || []
        return createJourneyTabs(solutions, selectedFare as Fare)
    }, [fareFamilyInfoData, selectedFare])

    const benefits = useMemo(() => {
        const fareBenefits = isRoundTrip ? selectedFare?.fareBenefitsInfo?.[tabValue + 1] : selectedFare?.fareBenefits
        if (!fareBenefits) return []
        return processBenefits(fareBenefits)
    }, [selectedFare, tabValue, processBenefits])

    return {
        isRoundTrip,
        fareFamilyEnabledOnReview,
        tabValue,
        isLoading,
        isError,
        journeyTabs,
        benefits,
        fareRuleExpanded,
        processedFareRules,
        showFareFamilyCards,
        showEditFares,
        selectedFare,
        handleFareRuleExpanded,
        handleTabChange,
        openFareFamilyModal,
        closeFareFamilyModal
    }
}

export default useTransparencyWidget
